import { useState, useEffect, useRef, useCallback } from "react";
import { Clock, MapPin, ScanFace, Play, Square, CheckCircle2, AlertTriangle, Loader2, Timer, RotateCcw, X } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useFaceVerification } from "@/hooks/useFaceVerification";
import FaceIDScanner from "@/components/app/FaceIDScanner";
import { toast } from "sonner";

interface ShiftClockSectionProps {
  officerRecord: {
    id: string;
    name: string;
    site_id: string | null;
    face_photo_url: string | null;
  };
  siteName: string;
  onClockedInChange?: (isClockedIn: boolean) => void;
}

type ClockState = "idle" | "verifying" | "verify_failed" | "clocked_in" | "clocking_out" | "clockout_verify_failed";

interface ActiveShift {
  id: string;
  clock_in: string;
  site_id: string | null;
}

const ShiftClockSection = ({ officerRecord, siteName, onClockedInChange }: ShiftClockSectionProps) => {
  const [clockState, setClockState] = useState<ClockState>("idle");
  const [activeShift, setActiveShift] = useState<ActiveShift | null>(null);
  const [elapsed, setElapsed] = useState("00:00:00");
  const [loading, setLoading] = useState(true);
  const [gpsStatus, setGpsStatus] = useState<"checking" | "valid" | "invalid" | "unavailable">("checking");
  const [currentCoords, setCurrentCoords] = useState<{ lat: number; lng: number } | null>(null);
  const [failMessage, setFailMessage] = useState<string | null>(null);

  const videoRef = useRef<HTMLVideoElement>(null);
  const { startCamera, stopCamera, verifyFace, isVerifying, scanningState, setError: setFaceError } = useFaceVerification();
  const elapsedTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Check for active shift on mount
  useEffect(() => {
    const checkActiveShift = async () => {
      const { data } = await supabase
        .from("shift_clock")
        .select("id, clock_in, site_id")
        .eq("officer_id", officerRecord.id)
        .eq("status", "clocked_in")
        .order("clock_in", { ascending: false })
        .limit(1)
        .maybeSingle();

      if (data) {
        setActiveShift(data);
        setClockState("clocked_in");
      }
      setLoading(false);
    };
    checkActiveShift();
  }, [officerRecord.id]);

  // Notify parent of clocked-in state changes
  useEffect(() => {
    onClockedInChange?.(clockState === "clocked_in");
  }, [clockState, onClockedInChange]);

  // Elapsed time timer
  useEffect(() => {
    if (activeShift && clockState === "clocked_in") {
      const updateElapsed = () => {
        const start = new Date(activeShift.clock_in).getTime();
        const now = Date.now();
        const diff = Math.floor((now - start) / 1000);
        const h = String(Math.floor(diff / 3600)).padStart(2, "0");
        const m = String(Math.floor((diff % 3600) / 60)).padStart(2, "0");
        const s = String(diff % 60).padStart(2, "0");
        setElapsed(`${h}:${m}:${s}`);
      };
      updateElapsed();
      elapsedTimerRef.current = setInterval(updateElapsed, 1000);
      return () => { if (elapsedTimerRef.current) clearInterval(elapsedTimerRef.current); };
    }
  }, [activeShift, clockState]);

  // GPS check
  useEffect(() => {
    if (!navigator.geolocation) {
      setGpsStatus("unavailable");
      return;
    }
    setGpsStatus("checking");
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        setCurrentCoords({ lat: pos.coords.latitude, lng: pos.coords.longitude });
        if (!officerRecord.site_id) {
          setGpsStatus("valid"); // No site assigned — GPS captured, allow clock in
          return;
        }
        // Get site location
        const { data: site } = await supabase.from("sites").select("lat, lng").eq("id", officerRecord.site_id!).maybeSingle();
        if (site?.lat && site?.lng) {
          const dist = getDistanceMeters(pos.coords.latitude, pos.coords.longitude, site.lat, site.lng);
          setGpsStatus(dist <= 500 ? "valid" : "invalid"); // 500m radius
        } else {
          setGpsStatus("valid"); // No coordinates set on site, allow
        }
      },
      () => {
        // GPS denied/failed — still allow clock-in, just without coordinates
        setGpsStatus("valid");
      },
      { enableHighAccuracy: true, timeout: 15000 }
    );
  }, [officerRecord.site_id]);

  const getDistanceMeters = (lat1: number, lng1: number, lat2: number, lng2: number) => {
    const R = 6371000;
    const dLat = ((lat2 - lat1) * Math.PI) / 180;
    const dLng = ((lng2 - lng1) * Math.PI) / 180;
    const a = Math.sin(dLat / 2) ** 2 + Math.cos((lat1 * Math.PI) / 180) * Math.cos((lat2 * Math.PI) / 180) * Math.sin(dLng / 2) ** 2;
    return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  };

  const handleClockIn = async () => {
    if (!officerRecord.face_photo_url) {
      toast.error("No reference photo on file. Contact your supervisor.");
      return;
    }
    if (gpsStatus === "invalid") {
      toast.error("You must be at your assigned site to clock in.");
      return;
    }

    setClockState("verifying");
    const video = videoRef.current;
    if (!video) return;

    const cameraOk = await startCamera(video);
    if (!cameraOk) {
      toast.error(faceError || "Camera access denied");
      setClockState("idle");
      return;
    }

    try {
      const result = await verifyFace(officerRecord.face_photo_url!, {
        officerId: officerRecord.id,
        officerName: officerRecord.name,
        siteId: officerRecord.site_id,
      });

      // Keep showing result in scanner for 2s
      setTimeout(() => stopCamera(), 2000);

      if (result.match && result.liveness && result.confidence >= 70) {
        // Insert clock-in record
        const { data: inserted, error } = await supabase
          .from("shift_clock")
          .insert({
            officer_id: officerRecord.id,
            site_id: officerRecord.site_id,
            clock_in_lat: currentCoords?.lat,
            clock_in_lng: currentCoords?.lng,
            clock_in_face_confidence: result.confidence,
            status: "clocked_in",
          })
          .select("id, clock_in, site_id")
          .single();

        if (error) {
          toast.error("Failed to clock in. Try again.");
          setClockState("idle");
          return;
        }

        setActiveShift(inserted);
        setClockState("clocked_in");
        toast.success(`Clocked in at ${new Date().toLocaleTimeString()} — ${result.confidence}% face match`);
      } else {
        setTimeout(() => setClockState("verify_failed"), 2000);
        setFailMessage(result.reason || "Face verification failed.");
      }
    } catch {
      toast.error("Clock in failed. Try again.");
      setClockState("idle");
      stopCamera();
    }
  };

  const handleClockOut = async () => {
    if (!activeShift) return;
    if (!officerRecord.face_photo_url) {
      toast.error("No reference photo on file.");
      return;
    }

    setClockState("clocking_out");
    const video = videoRef.current;
    if (!video) return;

    const cameraOk = await startCamera(video);
    if (!cameraOk) {
      toast.error(faceError || "Camera access denied");
      setClockState("clocked_in");
      return;
    }

    try {
      const result = await verifyFace(officerRecord.face_photo_url!, {
        officerId: officerRecord.id,
        officerName: officerRecord.name,
        siteId: officerRecord.site_id,
      });

      // Keep showing result in scanner for 2s
      setTimeout(() => stopCamera(), 2000);

      if (result.match && result.liveness && result.confidence >= 70) {
        // Get current GPS
        let outLat: number | null = null;
        let outLng: number | null = null;
        try {
          const pos = await new Promise<GeolocationPosition>((resolve, reject) =>
            navigator.geolocation.getCurrentPosition(resolve, reject, { enableHighAccuracy: true, timeout: 10000 })
          );
          outLat = pos.coords.latitude;
          outLng = pos.coords.longitude;
        } catch {}

        const clockInTime = new Date(activeShift.clock_in).getTime();
        const totalHours = parseFloat(((Date.now() - clockInTime) / (1000 * 60 * 60)).toFixed(2));

        const { error } = await supabase
          .from("shift_clock")
          .update({
            clock_out: new Date().toISOString(),
            clock_out_lat: outLat,
            clock_out_lng: outLng,
            clock_out_face_confidence: result.confidence,
            total_hours: totalHours,
            status: "completed",
          })
          .eq("id", activeShift.id);

        if (error) {
          toast.error("Failed to clock out. Try again.");
          setClockState("clocked_in");
          return;
        }

        setActiveShift(null);
        setClockState("idle");
        setElapsed("00:00:00");
        toast.success(`Clocked out — ${totalHours.toFixed(1)} hours logged`);
      } else {
        setTimeout(() => setClockState("clockout_verify_failed"), 2000);
        setFailMessage(result.reason || "Face verification failed.");
      }
    } catch {
      toast.error("Clock out failed. Try again.");
      setClockState("clocked_in");
      stopCamera();
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="w-6 h-6 animate-spin text-[hsl(var(--app-gold))]" />
      </div>
    );
  }

  const isVerifyingState = clockState === "verifying" || clockState === "clocking_out" || clockState === "verify_failed" || clockState === "clockout_verify_failed";
  const isFailedState = clockState === "verify_failed" || clockState === "clockout_verify_failed";

  const handleRetryVerification = async () => {
    setFailMessage(null);
    const isClockOut = clockState === "clockout_verify_failed";
    setClockState(isClockOut ? "clocking_out" : "verifying");

    await new Promise(r => setTimeout(r, 1000));

    try {
      const result = await verifyFace(officerRecord.face_photo_url!, {
        officerId: officerRecord.id,
        officerName: officerRecord.name,
        siteId: officerRecord.site_id,
      });

      if (!result.liveness) {
        setFailMessage("Liveness check failed. Use your real face.");
        setClockState(isClockOut ? "clockout_verify_failed" : "verify_failed");
        return;
      }

      if (!result.match || result.confidence < 70) {
        setFailMessage(`Face not recognized (${result.confidence}%). Try better lighting.`);
        setClockState(isClockOut ? "clockout_verify_failed" : "verify_failed");
        return;
      }

      stopCamera();

      if (!isClockOut) {
        const { data: inserted, error } = await supabase
          .from("shift_clock")
          .insert({
            officer_id: officerRecord.id,
            site_id: officerRecord.site_id,
            clock_in_lat: currentCoords?.lat,
            clock_in_lng: currentCoords?.lng,
            clock_in_face_confidence: result.confidence,
            status: "clocked_in",
          })
          .select("id, clock_in, site_id")
          .single();
        if (error) { toast.error("Failed to clock in."); setClockState("idle"); return; }
        setActiveShift(inserted);
        setClockState("clocked_in");
        toast.success(`Clocked in — ${result.confidence}% face match`);
      } else {
        if (!activeShift) return;
        let outLat: number | null = null, outLng: number | null = null;
        try {
          const pos = await new Promise<GeolocationPosition>((res, rej) =>
            navigator.geolocation.getCurrentPosition(res, rej, { enableHighAccuracy: true, timeout: 10000 }));
          outLat = pos.coords.latitude; outLng = pos.coords.longitude;
        } catch {}
        const totalHours = parseFloat(((Date.now() - new Date(activeShift.clock_in).getTime()) / 3600000).toFixed(2));
        const { error } = await supabase.from("shift_clock").update({
          clock_out: new Date().toISOString(), clock_out_lat: outLat, clock_out_lng: outLng,
          clock_out_face_confidence: result.confidence, total_hours: totalHours, status: "completed",
        }).eq("id", activeShift.id);
        if (error) { toast.error("Failed to clock out."); setClockState("clocked_in"); return; }
        setActiveShift(null); setClockState("idle"); setElapsed("00:00:00");
        toast.success(`Clocked out — ${totalHours.toFixed(1)} hours logged`);
      }
    } catch {
      setFailMessage("Verification error. Please try again.");
      setClockState(isClockOut ? "clockout_verify_failed" : "verify_failed");
    }
  };

  const handleCancelVerification = () => {
    stopCamera();
    setFailMessage(null);
    setClockState(clockState === "clockout_verify_failed" ? "clocked_in" : "idle");
  };

  return (
    <div className="space-y-4">
      {/* Shift Status Card */}
      <div className="app-card rounded-2xl overflow-hidden">
        <div className="h-px" style={{ background: "linear-gradient(90deg, transparent, hsl(var(--app-gold)), transparent)" }} />
        <div className="p-6">
          <div className="flex items-center gap-3 mb-6">
            <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
              clockState === "clocked_in" 
                ? "bg-[hsl(var(--app-green)/0.15)] border border-[hsl(var(--app-green)/0.3)]" 
                : "bg-[hsl(var(--app-gold)/0.1)] border border-[hsl(var(--app-gold)/0.2)]"
            }`}>
              {clockState === "clocked_in" ? (
                <Timer className="w-6 h-6 text-[hsl(var(--app-green))]" />
              ) : (
                <Clock className="w-6 h-6 text-[hsl(var(--app-gold))]" />
              )}
            </div>
            <div>
              <h2 className="font-bebas text-xl text-[hsl(var(--app-text-primary))]">SHIFT CLOCK</h2>
              <p className="text-xs text-[hsl(var(--app-text-muted))]">
                {clockState === "clocked_in" ? "Currently on shift" : "Ready to start your shift"}
              </p>
            </div>
          </div>

          {/* Status Indicators */}
          <div className="grid grid-cols-2 gap-3 mb-6">
            <div className="app-card-nested rounded-xl p-3">
              <div className="flex items-center gap-2 mb-1">
                <MapPin className="w-3.5 h-3.5 text-[hsl(var(--app-text-muted))]" />
                <span className="text-[10px] font-semibold uppercase tracking-wider text-[hsl(var(--app-text-muted))]">Location</span>
              </div>
              <p className="text-sm font-medium text-[hsl(var(--app-text-primary))] truncate">{siteName || "No site assigned"}</p>
              <div className="flex items-center gap-1.5 mt-1">
                {gpsStatus === "checking" && <Loader2 className="w-3 h-3 animate-spin text-[hsl(var(--app-text-muted))]" />}
                {gpsStatus === "valid" && <CheckCircle2 className="w-3 h-3 text-[hsl(var(--app-green))]" />}
                {gpsStatus === "invalid" && <AlertTriangle className="w-3 h-3 text-[hsl(var(--app-red))]" />}
                {gpsStatus === "unavailable" && <AlertTriangle className="w-3 h-3 text-[hsl(var(--app-text-muted))]" />}
                <span className="text-[10px] text-[hsl(var(--app-text-muted))]">
                  {gpsStatus === "checking" ? "Checking GPS…" : gpsStatus === "valid" ? "At site" : gpsStatus === "invalid" ? "Not at site" : "GPS unavailable"}
                </span>
              </div>
            </div>
            <div className="app-card-nested rounded-xl p-3">
              <div className="flex items-center gap-2 mb-1">
                <ScanFace className="w-3.5 h-3.5 text-[hsl(var(--app-text-muted))]" />
                <span className="text-[10px] font-semibold uppercase tracking-wider text-[hsl(var(--app-text-muted))]">Verification</span>
              </div>
              <p className="text-sm font-medium text-[hsl(var(--app-text-primary))]">
                {officerRecord.face_photo_url ? "Face ID Ready" : "Not Set Up"}
              </p>
              <div className="flex items-center gap-1.5 mt-1">
                {officerRecord.face_photo_url ? (
                  <CheckCircle2 className="w-3 h-3 text-[hsl(var(--app-green))]" />
                ) : (
                  <AlertTriangle className="w-3 h-3 text-[hsl(var(--app-red))]" />
                )}
                <span className="text-[10px] text-[hsl(var(--app-text-muted))]">
                  {officerRecord.face_photo_url ? "Photo on file" : "Contact supervisor"}
                </span>
              </div>
            </div>
          </div>

          {/* Timer Display */}
          {clockState === "clocked_in" && (
            <div className="text-center mb-6">
              <div className="inline-flex items-center gap-3 px-6 py-4 rounded-2xl bg-[hsl(var(--app-green)/0.08)] border border-[hsl(var(--app-green)/0.2)]">
                <div className="w-3 h-3 rounded-full bg-[hsl(var(--app-green))] animate-pulse" />
                <span className="font-mono-data text-4xl font-bold text-[hsl(var(--app-green))] tracking-wider">{elapsed}</span>
              </div>
              <p className="text-[10px] text-[hsl(var(--app-text-muted))] mt-2">
                Clocked in at {activeShift ? new Date(activeShift.clock_in).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) : "—"}
              </p>
            </div>
          )}

          {/* Camera Preview — always mounted so ref is available */}
          <div className={`mb-6 ${isVerifyingState ? "" : "hidden"}`}>
            <FaceIDScanner 
              videoRef={videoRef} 
              state={scanningState} 
              error={failMessage} 
            />

            {/* Failure message */}
            {isFailedState && failMessage && (
              <div className="mt-3 p-3 rounded-xl bg-[hsl(var(--app-red)/0.08)] border border-[hsl(var(--app-red)/0.2)] flex items-start gap-2">
                <AlertTriangle className="w-4 h-4 text-[hsl(var(--app-red))] shrink-0 mt-0.5" />
                <p className="text-xs text-[hsl(var(--app-red))]">{failMessage}</p>
              </div>
            )}
          </div>

          {/* Action Buttons */}
          <div className="space-y-3">
            {clockState === "idle" && (
              <button
                onClick={handleClockIn}
                disabled={gpsStatus === "invalid" || !officerRecord.face_photo_url}
                className="w-full py-4 rounded-xl bg-[hsl(var(--app-green))] text-white font-bold text-lg hover:brightness-110 hover:-translate-y-0.5 transition-all disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-3"
                style={{ boxShadow: "0 4px 20px hsl(var(--app-green) / 0.3)" }}
              >
                <Play className="w-5 h-5" />
                Clock In
              </button>
            )}

            {clockState === "clocked_in" && (
              <button
                onClick={handleClockOut}
                className="w-full py-4 rounded-xl bg-[hsl(var(--app-red))] text-white font-bold text-lg hover:brightness-110 hover:-translate-y-0.5 transition-all flex items-center justify-center gap-3"
                style={{ boxShadow: "0 4px 20px hsl(var(--app-red) / 0.3)" }}
              >
                <Square className="w-5 h-5" />
                Clock Out
              </button>
            )}

            {isFailedState && (
              <div className="flex gap-3">
                <button
                  onClick={handleCancelVerification}
                  className="flex-1 py-3 rounded-xl border border-[hsl(var(--app-border-subtle))] text-sm text-[hsl(var(--app-text-muted))] hover:bg-[hsl(var(--app-surface-2))] transition-colors flex items-center justify-center gap-1.5"
                >
                  <X className="w-4 h-4" /> Cancel
                </button>
                <button
                  onClick={handleRetryVerification}
                  disabled={isVerifying}
                  className="flex-1 py-3 rounded-xl bg-[hsl(var(--app-gold))] text-[hsl(var(--app-bg))] font-semibold text-sm hover:brightness-110 transition-all flex items-center justify-center gap-2 disabled:opacity-50"
                >
                  <RotateCcw className="w-4 h-4" /> Retry Verification
                </button>
              </div>
            )}

            {(clockState === "verifying" || clockState === "clocking_out") && (
              <div className="flex items-center justify-center gap-2 py-4 text-[hsl(var(--app-gold))]">
                <Loader2 className="w-5 h-5 animate-spin" />
                <span className="text-sm font-medium">{clockState === "verifying" ? "Verifying for clock in…" : "Verifying for clock out…"}</span>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Today's Shift History */}
      <TodayShifts officerId={officerRecord.id} siteName={siteName} />
    </div>
  );
};

// Sub-component: Today's completed shifts
const TodayShifts = ({ officerId, siteName }: { officerId: string; siteName: string }) => {
  const [shifts, setShifts] = useState<any[]>([]);

  useEffect(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const fetchShifts = async () => {
      const { data } = await supabase
        .from("shift_clock")
        .select("*")
        .eq("officer_id", officerId)
        .gte("clock_in", today.toISOString())
        .order("clock_in", { ascending: false });
      setShifts(data || []);
    };
    fetchShifts();

    // Realtime updates
    const channel = supabase
      .channel(`shift-clock-${officerId}`)
      .on("postgres_changes", { event: "*", schema: "public", table: "shift_clock", filter: `officer_id=eq.${officerId}` }, () => fetchShifts())
      .subscribe();

    return () => { supabase.removeChannel(channel); };
  }, [officerId]);

  const completedShifts = shifts.filter(s => s.status === "completed");

  if (completedShifts.length === 0) return null;

  const totalHours = completedShifts.reduce((sum, s) => sum + (s.total_hours || 0), 0);

  return (
    <div className="app-card rounded-2xl p-5">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-bebas text-base text-[hsl(var(--app-text-primary))]">TODAY'S SHIFTS</h3>
        <span className="text-xs px-2 py-0.5 rounded-full bg-[hsl(var(--app-gold)/0.15)] text-[hsl(var(--app-gold))] font-medium">
          {totalHours.toFixed(1)}h total
        </span>
      </div>
      <div className="space-y-2">
        {completedShifts.map(shift => {
          const inTime = new Date(shift.clock_in).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
          const outTime = shift.clock_out ? new Date(shift.clock_out).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) : "—";
          return (
            <div key={shift.id} className="flex items-center justify-between app-card-nested rounded-lg px-3 py-2.5">
              <div className="flex items-center gap-3">
                <CheckCircle2 className="w-4 h-4 text-[hsl(var(--app-green))]" />
                <div>
                  <p className="text-xs font-medium text-[hsl(var(--app-text-primary))]">{inTime} — {outTime}</p>
                  <p className="text-[10px] text-[hsl(var(--app-text-muted))]">{siteName}</p>
                </div>
              </div>
              <div className="text-right">
                <p className="text-xs font-bold text-[hsl(var(--app-gold))]">{(shift.total_hours || 0).toFixed(1)}h</p>
                <p className="text-[10px] text-[hsl(var(--app-text-muted))]">{shift.clock_in_face_confidence}% match</p>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default ShiftClockSection;
